import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogActions,
  Button, TextField, IconButton, Slide, Typography,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import axios from 'axios';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles(() => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  content: {
    padding: '0 16px 16px',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
    marginTop: 16,
  },
  photoGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  photoThumb: {
    width: 64,
    height: 64,
    borderRadius: 6,
    objectFit: 'cover',
    border: '1px solid #e0e0e0',
    cursor: 'pointer',
  },
  photoWrapper: {
    position: 'relative',
    width: 64,
    height: 64,
  },
  removeBtn: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 18,
    height: 18,
    background: '#e53935',
    color: '#fff',
    fontSize: 10,
    padding: 0,
    '&:hover': { background: '#c62828' },
  },
  addTile: {
    width: 64,
    height: 64,
    borderRadius: 6,
    border: '1px dashed #ccc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: '#fafafa',
    '&:hover': { background: '#f0f0f0', borderColor: '#999' },
  },
}));

const HerbicideNotesModal = ({ open, onClose, herbicideNotes, herbicidePhotos, areaId, onSave }) => {
  const classes = useStyles();
  const fileRef = useRef();
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setNotes(herbicideNotes || '');
      setPhotos(herbicidePhotos || []);
    }
  }, [open, herbicideNotes, herbicidePhotos]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await axios.post('/api/uploads/herbicide-photo-url', {
        fileName: file.name,
        contentType: file.type,
        areaId,
      });
      await axios.put(data.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
      });
      setPhotos(prev => [...prev, {
        url: data.objectUrl,
        fileName: file.name,
        uploadedAt: new Date().toISOString(),
      }]);
    } catch (err) {
      console.error('Herbicide photo upload failed:', err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemovePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onSave({ herbicideNotes: notes, herbicidePhotos: photos });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideUp}
      PaperProps={{ style: { borderRadius: 12, minWidth: 340, maxWidth: 420 } }}
    >
      <div className={classes.title}>
        <Typography style={{ fontSize: 15, fontWeight: 600 }}>Herbicide Notes</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.content}>
        <Typography className={classes.sectionLabel} style={{ marginTop: 0 }}>Notes</Typography>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          multiline
          minRows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Special instructions for herbicide application..."
        />

        <Typography className={classes.sectionLabel}>Photos</Typography>
        <div className={classes.photoGrid}>
          {photos.map((photo, idx) => (
            <div key={photo.url || idx} className={classes.photoWrapper}>
              <img
                src={photo.url}
                alt={photo.fileName || 'Herbicide note photo'}
                className={classes.photoThumb}
              />
              <IconButton
                className={classes.removeBtn}
                size="small"
                onClick={() => handleRemovePhoto(idx)}
              >
                ×
              </IconButton>
            </div>
          ))}
          <div className={classes.addTile} onClick={() => !uploading && fileRef.current?.click()}>
            {uploading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <AddPhotoAlternateIcon style={{ fontSize: 20, color: '#999' }} />
                <span style={{ fontSize: 9, color: '#999', marginTop: 2 }}>Add</span>
              </>
            )}
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
      </div>

      <DialogActions style={{ padding: '12px 16px', gap: 8 }}>
        <Button
          onClick={onClose}
          style={{ textTransform: 'none', color: '#666', flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          style={{
            textTransform: 'none',
            fontWeight: 600,
            flex: 1,
            borderRadius: 8,
            boxShadow: 'none',
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HerbicideNotesModal;
